import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Edit3, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { OwnedEntity } from "../../lib/contracts";
import { entityEndpoint } from "../../lib/contracts";
import { ApiError, request } from "../../lib/api";
import { formatRelative } from "../../lib/format";
import { useCollection } from "../../hooks/useCollection";
import { useOnline } from "../../hooks/useOnline";
import { useToast } from "../../contexts/ToastContext";
import { EmptyState, ErrorState, LoadingState } from "../ui/AsyncState";
import { ConfirmDialog, Dialog } from "../ui/Dialog";
import { PageHeader } from "../ui/PageHeader";
import { StatusBadge } from "../ui/Status";

export type FieldType = "text" | "textarea" | "url" | "date" | "datetime-local" | "number" | "select" | "checkbox";
export interface FieldDefinition {
  key: string;
  label: string;
  type?: FieldType;
  required?: boolean;
  help?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{ value: string; label: string }>;
  full?: boolean;
}

type DraftValue = string | boolean;
export interface ResourceDefinition<T extends OwnedEntity> {
  endpoint: string;
  collection: string;
  basePath: string;
  title: string;
  description: string;
  createLabel: string;
  emptyTitle: string;
  emptyDescription: string;
  fields: FieldDefinition[];
  initial: Record<string, DraftValue>;
  toDraft: (item: T) => Record<string, DraftValue>;
  toPayload: (draft: Record<string, DraftValue>, current: T | null) => Record<string, unknown>;
  renderTitle: (item: T) => string;
  renderSummary: (item: T) => ReactNode;
  renderMeta: (item: T) => ReactNode;
  secondaryAction?: ReactNode;
  renderOverview?: (items: T[]) => ReactNode;
  filter?: { key: string; label: string; options: Array<{ value: string; label: string }> };
  queryDefaults?: Record<string, string>;
}

function ResourceForm({ fields, draft, errors, onChange }: { fields: FieldDefinition[]; draft: Record<string, DraftValue>; errors: Record<string, string[]>; onChange: (key: string, value: DraftValue) => void }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => {
        const id = `resource-${field.key}`;
        const error = errors[field.key]?.[0];
        const common = { id, required: field.required, disabled: false, "aria-invalid": Boolean(error), "aria-describedby": error ? `${id}-error` : field.help ? `${id}-help` : undefined };
        return (
          <label key={field.key} className={`field ${field.full || field.type === "textarea" ? "sm:col-span-2" : ""}`} htmlFor={id}>
            <span className="field-label">{field.label}{field.required ? "（必填）" : ""}</span>
            {field.type === "textarea" ? (
              <textarea {...common} className="textarea" value={String(draft[field.key] ?? "")} onChange={(event) => onChange(field.key, event.target.value)} />
            ) : field.type === "select" ? (
              <select {...common} className="input" value={String(draft[field.key] ?? "")} onChange={(event) => onChange(field.key, event.target.value)}>
                {field.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            ) : field.type === "checkbox" ? (
              <span className="flex min-h-11 items-center gap-3 rounded-lg border bg-surface px-3">
                <input {...common} className="h-5 w-5 accent-[var(--color-primary)]" type="checkbox" checked={Boolean(draft[field.key])} onChange={(event) => onChange(field.key, event.target.checked)} />
                <span className="text-sm text-muted">启用</span>
              </span>
            ) : (
              <input {...common} className="input" type={field.type ?? "text"} value={String(draft[field.key] ?? "")} min={field.min} max={field.max} step={field.step} onChange={(event) => onChange(field.key, event.target.value)} />
            )}
            {field.help ? <span id={`${id}-help`} className="text-xs text-muted">{field.help}</span> : null}
            {error ? <span id={`${id}-error`} className="text-xs font-medium text-danger">{error}</span> : null}
          </label>
        );
      })}
    </div>
  );
}

export function ResourcePage<T extends OwnedEntity>({ definition }: { definition: ResourceDefinition<T> }) {
  const [params, setParams] = useSearchParams();
  const routeParams = useParams();
  const navigate = useNavigate();
  const online = useOnline();
  const { notify } = useToast();
  const q = params.get("q") ?? "";
  const filterValue = definition.filter ? params.get(definition.filter.key) ?? "" : "";
  const query = useMemo(() => ({ ...definition.queryDefaults, q, ...(definition.filter ? { [definition.filter.key]: filterValue } : {}) }), [q, filterValue, definition.filter, definition.queryDefaults]);
  const { items, loading, refreshing, error, reload } = useCollection<T>(definition.endpoint, query);
  const [editing, setEditing] = useState<T | null>(null);
  const [draft, setDraft] = useState<Record<string, DraftValue>>(definition.initial);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<unknown>(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const isNew = routeParams.id === "new";
  const dialogOpen = isNew || Boolean(editing);

  useEffect(() => {
    if (!routeParams.id || routeParams.id === "new") { if (!routeParams.id) setEditing(null); return; }
    const item = items.find((entry) => entry.id === routeParams.id) ?? null;
    if (item) { setEditing(item); setDraft(definition.toDraft(item)); }
  }, [routeParams.id, items, definition]);
  useEffect(() => { if (isNew) { setEditing(null); setDraft({ ...definition.initial }); setFieldErrors({}); setFormError(null); } }, [isNew, definition.initial]);

  const openCreate = () => navigate(`${definition.basePath}/new`);
  const openEdit = (item: T) => { setEditing(item); setDraft(definition.toDraft(item)); setFieldErrors({}); setFormError(null); navigate(`${definition.basePath}/${item.id}`); };
  const closeForm = () => { if (!saving) navigate(definition.basePath); };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!online) { setFormError("当前离线，无法保存。请恢复网络后重试。"); return; }
    const missing = definition.fields.filter((field) => field.required && !String(draft[field.key] ?? "").trim());
    if (missing.length) { setFieldErrors(Object.fromEntries(missing.map((field) => [field.key, [`请填写${field.label}。`]]))); return; }
    setSaving(true); setFieldErrors({}); setFormError(null);
    try {
      const path = editing ? entityEndpoint(definition.collection, editing.id) : definition.endpoint;
      await request<T>(path, { method: editing ? "PATCH" : "POST", body: definition.toPayload(draft, editing) });
      notify(editing ? "更改已保存到云端。" : "内容已创建并保存到云端。");
      await reload(); navigate(definition.basePath, { replace: true });
    } catch (reason) {
      if (reason instanceof ApiError) {
        if (reason.status === 409) setConflict(reason.current ?? {});
        else { setFieldErrors(reason.fieldErrors ?? {}); setFormError(reason.message); }
      } else setFormError("保存失败，请稍后重试。");
    } finally { setSaving(false); }
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await request<void>(entityEndpoint(definition.collection, deleting.id), { method: "DELETE" });
      notify(`“${definition.renderTitle(deleting)}”已移到回收站。`);
      setDeleting(null); await reload();
    } catch (reason) { notify(reason instanceof Error ? reason.message : "删除失败，请重试。", "error"); }
    finally { setDeleteBusy(false); }
  };

  return (
    <div className="page-container">
      <PageHeader title={definition.title} description={definition.description} action={<div className="flex flex-wrap gap-2">{definition.secondaryAction}<button className="btn-primary flex-1 sm:flex-none" disabled={!online} onClick={openCreate}><Plus size={18} />{definition.createLabel}</button></div>} />
      {definition.renderOverview ? <div className="mb-5">{definition.renderOverview(items)}</div> : null}
      <div className="card mb-5 flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
        <label className="relative min-w-0 flex-1"><span className="sr-only">搜索{definition.title}</span><Search className="absolute left-3 top-3 text-muted" size={18} aria-hidden="true" /><input className="input pl-10" value={q} onChange={(event) => { const next = new URLSearchParams(params); event.target.value ? next.set("q", event.target.value) : next.delete("q"); setParams(next, { replace: true }); }} placeholder={`搜索${definition.title}`} /></label>
        {definition.filter ? <label className="field sm:min-w-44"><span className="sr-only">{definition.filter.label}</span><select className="input" value={filterValue} onChange={(event) => { const next = new URLSearchParams(params); event.target.value ? next.set(definition.filter!.key, event.target.value) : next.delete(definition.filter!.key); setParams(next, { replace: true }); }}><option value="">全部{definition.filter.label}</option>{definition.filter.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label> : null}
        <button className="btn-ghost" onClick={() => void reload(true)} disabled={refreshing}><RefreshCw size={17} className={refreshing ? "animate-spin" : ""} />刷新</button>
      </div>
      <p className="mb-3 text-sm text-muted" role="status" aria-live="polite">{loading ? "正在读取…" : `共 ${items.length} 条记录`}</p>
      {loading ? <LoadingState /> : error ? <ErrorState message={error} onRetry={() => void reload()} /> : items.length === 0 ? <EmptyState title={q || filterValue ? "没有符合条件的结果" : definition.emptyTitle} description={q || filterValue ? "清除搜索或筛选条件后再试。" : definition.emptyDescription} action={q || filterValue ? <button className="btn-secondary" onClick={() => setParams({})}>清除筛选</button> : <button className="btn-primary" disabled={!online} onClick={openCreate}>{definition.createLabel}</button>} /> : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-4">
          {items.map((item) => (
            <article key={item.id} className="card flex min-h-44 flex-col p-4 transition-colors hover:border-[var(--color-border-strong)]">
              <div className="flex items-start gap-3"><div className="min-w-0 flex-1"><h2 className="break-words text-lg font-semibold">{definition.renderTitle(item)}</h2><div className="mt-2 text-sm leading-relaxed text-muted">{definition.renderSummary(item)}</div></div><StatusBadge>{formatRelative(item.updatedAt)}</StatusBadge></div>
              <div className="mt-4 flex-1 text-sm">{definition.renderMeta(item)}</div>
              <div className="mt-4 flex items-center justify-end gap-2 border-t pt-3"><button className="btn-ghost" onClick={() => openEdit(item)}><Edit3 size={17} />编辑</button><button className="btn-ghost text-danger" onClick={() => setDeleting(item)}><Trash2 size={17} />删除</button></div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} title={editing ? `编辑${definition.title.replace(/^工作|学习/, "")}` : definition.createLabel} description="保存后会写入云端，并在其他设备登录时可见。" onClose={closeForm} preventClose={saving} footer={
        <><button className="btn-secondary" onClick={closeForm} disabled={saving}>取消</button><button className="btn-primary" onClick={() => document.getElementById("resource-form-submit")?.click()} disabled={saving || !online}>{saving ? "正在保存…" : "保存更改"}</button></>
      }>
        <form id="resource-form" onSubmit={submit} noValidate>
          {formError ? <div className="mb-4 rounded-lg bg-[var(--color-danger-soft)] p-3 text-sm text-danger" role="alert">{formError}</div> : null}
          <ResourceForm fields={definition.fields} draft={draft} errors={fieldErrors} onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))} />
          <button id="resource-form-submit" type="submit" className="sr-only">提交</button>
        </form>
      </Dialog>
      <Dialog open={conflict !== null} title="内容已在其他设备修改" description="为避免覆盖云端数据，请先使用最新版本。" onClose={() => setConflict(null)} wide footer={<><button className="btn-secondary" onClick={() => setConflict(null)}>保留当前输入</button><button className="btn-primary" onClick={() => { if (conflict && typeof conflict === "object") { setDraft((current) => ({ ...current, ...conflict as Record<string, DraftValue> })); setConflict(null); } }}>使用云端版本</button></>}>
        <div className="grid gap-4 md:grid-cols-2"><section className="rounded-lg border p-4"><h3 className="font-semibold">你的内容</h3><pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-words text-xs text-muted">{JSON.stringify(draft, null, 2)}</pre></section><section className="rounded-lg border p-4"><h3 className="font-semibold">云端内容</h3><pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-words text-xs text-muted">{JSON.stringify(conflict, null, 2)}</pre></section></div>
      </Dialog>
      <ConfirmDialog open={Boolean(deleting)} title={deleting ? `删除“${definition.renderTitle(deleting)}”` : "删除内容"} description="这项内容会移到回收站，之后仍可从设置中恢复。" busy={deleteBusy} onClose={() => setDeleting(null)} onConfirm={confirmDelete} />
    </div>
  );
}
